// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, StateRow } from './types';
import { esc, num, rate, pct, times, delta, COL } from './format';
import { gloss } from './glossary';
import { sparkline } from './charts';
import { rankBy } from './analysis';

function rankLabel(states: StateRow[], code: string, get: (s: StateRow) => number | null): string {
  const ranked = rankBy(states, get);
  const i = ranked.findIndex((s) => s.code === code);
  return i < 0 ? '—' : `${i + 1} of ${ranked.length}`;
}

export function renderStateDrawer(drawer: HTMLElement, s: StateRow, data: Dataset): void {
  const states = data.states;
  const c = data.meta.counts;
  const j = s.years.length - 1;
  // mini dumbbell: non-Indigenous vs Aboriginal & Torres Strait Islander (age-standardised)
  const maxRate = Math.max(s.atsiRateStd ?? 0, c.atsiRateStd ?? 0, 1);
  const barW = (v: number | null) => (v == null ? 0 : Math.max(2, (v / maxRate) * 100));

  drawer.innerHTML = `
    <button class="icon-btn drawer-close" aria-label="Close detail">✕</button>
    <h2>${esc(s.name)}</h2>
    <p class="sub">${num(s.totalNow)} prisoners on 30 June ${data.meta.latestYear} · ${gloss('imprisonmentRate', 'rate')} ${rate(s.rateNow)} per 100,000</p>

    <dl class="kv">
      <dt>Imprisonment rate (per 100k)</dt><dd>${rate(s.rateNow)}</dd>
      <dt>Rank by rate</dt><dd>${esc(rankLabel(states, s.code, (x) => x.rateNow))}</dd>
      <dt>Total prisoners</dt><dd>${num(s.totalNow)}</dd>
      <dt>Change since ${data.meta.firstYear}</dt><dd class="${(s.growthPct ?? 0) >= 0 ? 'up' : 'down'}">${delta(s.growthPct)}</dd>
      <dt>${gloss('atsi', 'Aboriginal & Torres Strait Islander')} share</dt><dd>${pct(s.indigShareNow)}</dd>
      <dt>${gloss('remand', 'Remand')} share</dt><dd>${pct(s.remandShareNow)}</dd>
      <dt>${gloss('prior', 'Prior imprisonment')}</dt><dd>${pct(s.priorPctNow)}</dd>
      <dt>Median time on remand</dt><dd>${s.remandMedian == null ? '—' : `${rate(s.remandMedian)} mo`}</dd>
    </dl>

    <h3>Over-representation (${gloss('ageStandardised', 'age-standardised')})</h3>
    <div style="display:flex;flex-direction:column;gap:8px">
      <div style="display:grid;grid-template-columns:minmax(0,150px) 1fr auto;align-items:center;gap:8px">
        <span style="min-width:0;font-size:var(--font-size-sm)">Non-Indigenous</span>
        <span style="height:14px;background:var(--bg-elevated);border-radius:3px;overflow:hidden"><span style="display:block;height:100%;width:${barW(s.nonIndigRateStd)}%;background:${COL.nonIndig}"></span></span>
        <span class="num" style="font-size:var(--font-size-sm);min-width:52px;text-align:right">${rate(s.nonIndigRateStd, 0)}</span>
      </div>
      <div style="display:grid;grid-template-columns:minmax(0,150px) 1fr auto;align-items:center;gap:8px">
        <span style="min-width:0;font-size:var(--font-size-sm)">Aboriginal &amp; T.S.I.</span>
        <span style="height:14px;background:var(--bg-elevated);border-radius:3px;overflow:hidden"><span style="display:block;height:100%;width:${barW(s.atsiRateStd)}%;background:${COL.atsi}"></span></span>
        <span class="num" style="font-size:var(--font-size-sm);min-width:52px;text-align:right">${rate(s.atsiRateStd, 0)}</span>
      </div>
    </div>
    <p class="mini-note">Ratio ${times(s.ratioStd)} the non-Indigenous rate (national ${times(c.ratioStd)}). Crude ratio ${times(s.ratioCrude)}.</p>

    <h3>Imprisonment rate, ${s.years[0]}–${s.years[j]}</h3>
    ${sparkline(s.totalRate, COL.total, 240, 46)}
    <p class="mini-note">Per 100,000 adults. ${rate(s.totalRate[0])} → ${rate(s.totalRate[j])}.</p>

    <p class="mini-note" style="margin-top:16px">This measures imprisonment, not offending. Counts are rounded and small cells may be perturbed for privacy.</p>
  `;
}
